document.addEventListener('alpine:init', () => {
    Alpine.data('powerSearch', () => ({
        searchOpen: false,
        searchQuery: '',
        searchResults: [],
        searchFocused: false,
        isSearching: false,
        burgerMenuOpen: true,

        // === MOBILE FOCUS HELPER ===
        focusInputOnMobile() {
            const isMobile = window.innerWidth < 1024;
            if (isMobile && this.searchOpen && this.searchQuery.length === 0) {
                setTimeout(() => {
                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.focus();
                        this.searchFocused = true;
                        document.body.classList.add('search-focused');
                    }
                }, 300);
            }
        },

        // === TOGGLE HEADER ELEMENTS ===
        toggleHeaderElements(hide) {
            const isMobile = window.innerWidth < 1024;
            if (!isMobile) return;

            // Target the logo inside the burger menu modal specifically
            const burgerModal = document.querySelector('[role="dialog"][aria-modal="true"]');
            if (!burgerModal) return;

            const logo = burgerModal.querySelector('header.bg-gray a.absolute.left-4');
            const ctaButton = burgerModal.querySelector('header.bg-gray a#free-quote');

            [logo, ctaButton].forEach((el) => {
                if (el) {
                    el.style.transition = 'opacity 0.25s ease, visibility 0.25s ease';
                    el.style.opacity = hide ? '0' : '1';
                    el.style.visibility = hide ? 'hidden' : 'visible';
                    el.style.display = hide ? 'none' : 'block';
                }
            });
        },

        // === RESET SEARCH STATE (when burger closes) ===
        resetSearchState() {
            const isMobile = window.innerWidth < 1024;

            this.burgerMenuOpen = false;
            this.searchFocused = false;
            document.body.classList.remove('search-focused', 'search-typing');
            if (!isMobile) {
                document.body.style.overflow = '';
                return;
            }

            // Clear the search query and results
            this.searchQuery = '';
            this.searchResults = [];
            this.isSearching = false;

            this.toggleHeaderElements(false);

            setTimeout(() => {
                this.toggleHeaderElements(false);
            }, 100);
        },

        // === HIGHLIGHT SEARCH TERM ===
        highlightSearchTerm(text, searchTerm) {
            if (!text || !searchTerm) return text;
            const regex = new RegExp(`(${searchTerm})`, 'gi');
            return text.replace(regex, '<mark style="background-color: #D4FF00; font-weight: 600;">$1</mark>');
        },

        init() {
            const isMobile = window.innerWidth < 1024;
            if (isMobile) {
                this.searchOpen = true;
                document.body.classList.add('search-ready');
            }

            // Listen for burger menu close event
            window.addEventListener('burger-closed', () => this.resetSearchState());

            // Listen for burger menu open event
            window.addEventListener('burger-opened', () => {
                this.burgerMenuOpen = true;
            });

            this.$watch('searchOpen', (value) => {
                const isMobile = window.innerWidth < 1024;
                if (value) {
                    // Force everything to happen synchronously - no delays
                    requestAnimationFrame(() => {
                        document.body.classList.add('search-active');
                        if (!isMobile) {
                            document.body.style.overflow = 'hidden';
                        }
                    });

                    if (!isMobile) {
                        this.$nextTick(() => {
                            if (this.$refs.searchInput) {
                                this.$refs.searchInput.focus();
                                this.searchFocused = true;
                                document.body.classList.add('search-focused');
                            }
                        });
                    }
                } else {
                    document.body.classList.remove('search-active', 'search-typing', 'search-focused');
                    this.searchFocused = false;
                    this.toggleHeaderElements(false);
                    document.body.style.overflow = '';
                }
            });

            // === WATCH TYPING ===
            this.$watch('searchQuery', (value) => {
                const isMobile = window.innerWidth < 1024;
                if (isMobile) {
                    document.body.classList.toggle('search-typing', value.length > 0);
                }
            });

            // === WATCH FOCUS STATE ===
            this.$watch('searchFocused', (value) => {
                const isMobile = window.innerWidth < 1024;
                if (!isMobile) return;

                if (this.burgerMenuOpen && value) {
                    this.toggleHeaderElements(true);
                } else {
                    this.toggleHeaderElements(false);
                }
            });

            // === HANDLE RESIZE ===
            let resizeTimer;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimer);
                resizeTimer = setTimeout(() => {
                    const isMobile = window.innerWidth < 1024;
                    if (isMobile) {
                        this.searchOpen = true;
                        document.body.style.overflow = '';
                        if (!this.searchFocused) this.toggleHeaderElements(false);
                    } else {
                        if (!this.searchQuery) this.searchOpen = false;
                        this.toggleHeaderElements(false);
                    }
                }, 150);
            });
        },

        // === FOCUS HANDLER ===
        onSearchFocus() {
            this.searchFocused = true;
            const isMobile = window.innerWidth < 1024;

            if (isMobile) {
                document.body.classList.add('search-focused');

                if (this.burgerMenuOpen) {
                    this.toggleHeaderElements(true);
                } else {
                    this.toggleHeaderElements(false);
                }
            }
        },

        // === BLUR HANDLER ===
        onSearchBlur() {
            setTimeout(() => {
                if (this.searchQuery.length === 0) {
                    this.searchFocused = false;
                    document.body.classList.remove('search-focused');

                    if (!this.burgerMenuOpen) {
                        this.toggleHeaderElements(false);
                    }
                }
            }, 200);
        },

        // === AJAX SEARCH ===
        async performSearch() {
            const query = this.searchQuery.trim();

            // Clear results and return early if less than 3 characters - NO AJAX CALL
            if (query.length < 3) {
                this.searchResults = [];
                this.isSearching = false;
                return;
            }

            this.isSearching = true;

            try {
                const formData = new FormData();
                formData.append('action', 'power_search');
                formData.append('nonce', powerSearch.nonce);
                formData.append('query', query);

                const response = await fetch(powerSearch.ajaxUrl, { method: 'POST', body: formData });
                const data = await response.json();
                this.searchResults = data.results || [];
            } catch (error) {
                console.error('Search error:', error);
                this.searchResults = [];
            } finally {
                this.isSearching = false;
            }
        },

        // === CLEAR SEARCH ===
        clearSearch() {
            this.searchQuery = '';
            this.searchResults = [];
            this.isSearching = false;
            document.body.classList.remove('search-typing');

            if (!this.burgerMenuOpen) {
                this.searchFocused = false;
                document.body.classList.remove('search-focused');
                this.toggleHeaderElements(false);
            }

            this.$nextTick(() => this.$refs.searchInput?.focus());
        },

        // === CLOSE SEARCH (DESKTOP ONLY) ===
        closeSearch() {
            const isMobile = window.innerWidth < 1024;
            if (!isMobile) {
                this.searchOpen = false;
                this.searchQuery = '';
                this.searchResults = [];
                this.isSearching = false;
                this.searchFocused = false;
                document.body.classList.remove('search-active', 'search-typing', 'search-focused');
                document.body.style.overflow = '';
            }
        },

        // === RESULT CLICK ===
        handleResultClick() {
            document.body.style.overflow = '';
            return true;
        }
    }));
});
